import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

// Importaciones de MUI
import {
  Button,
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack
} from '@mui/material';
import {
  DownloadForOffline as DownloadIcon,
  CloudUpload as UploadIcon,
  CompareArrows as CompareArrowsIcon,
  PlaylistAddCheck as CheckIcon,
  Info as InfoIcon
} from '@mui/icons-material';

// Importación de la librería para generar Excel
import * as XLSX from 'xlsx';

import { ReactSpreadsheetImport } from "react-spreadsheet-import";
import { useCompanyCore } from '../../../../core/modules/company';
import { useTransactionsCore } from '../../../../core/modules/transactions';

// El objeto de traducciones permanece igual
export const translations = {
  // ... (tu objeto de traducciones completo va aquí, lo he omitido por brevedad)
  uploadStep: {
    title: "Subir archivo",
    manifestTitle: "Datos que esperamos:",
    manifestDescription: "(Tendrá la oportunidad de renombrar o eliminar columnas en los siguientes pasos)",
    maxRecordsExceeded: (maxRecords: any) => `Demasiados registros. Se permite un máximo de ${maxRecords}`,
    dropzone: {
      title: "Subir archivo .xlsx, .xls o .csv",
      errorToastDescription: "subida rechazada",
      activeDropzoneTitle: "Suelte el archivo aquí...",
      buttonTitle: "Seleccionar archivo",
      loadingTitle: "Procesando...",
    },
  },
  selectHeaderStep: {
    title: "Seleccionar fila de encabezado",
    nextButtonTitle: "Siguiente",
    backButtonTitle: "Atrás",
  },
  matchColumnsStep: {
    title: "Hacer coincidir columnas",
    nextButtonTitle: "Siguiente",
    backButtonTitle: "Atrás",
    userTableTitle: "Su tabla",
    templateTitle: "Se convertirá en",
    selectPlaceholder: "Seleccionar columna...",
    ignoredColumnText: "Columna ignorada",
    subSelectPlaceholder: "Seleccionar...",
    matchDropdownTitle: "Coincidir",
    unmatched: "No coincide",
    duplicateColumnWarningTitle: "Otra columna no seleccionada",
    duplicateColumnWarningDescription: "Las columnas no pueden duplicarse",
  },
  validationStep: {
    title: "Validar datos",
    nextButtonTitle: "Confirmar",
    backButtonTitle: "Atrás",
    noRowsMessage: "No se encontraron datos",
    noRowsMessageWhenFiltered: "No hay datos que contengan errores",
    discardButtonTitle: "Descartar filas seleccionadas",
    filterSwitchTitle: "Mostrar solo filas con errores",
  },
  alerts: {
    confirmClose: {
      headerTitle: "Salir del flujo de importación",
      bodyText: "¿Está seguro? Su información actual no se guardará.",
      cancelButtonTitle: "Cancelar",
      exitButtonTitle: "Salir del flujo",
    },
    submitIncomplete: {
      headerTitle: "Errores detectados",
      bodyText: "Todavía hay algunas filas que contienen errores. Las filas con errores serán ignoradas al enviar.",
      bodyTextSubmitForbidden: "Todavía hay algunas filas que contienen errores.",
      cancelButtonTitle: "Cancelar",
      finishButtonTitle: "Enviar",
    },
    submitError: {
      title: "Error",
      defaultMessage: "Ocurrió un error al enviar los datos",
    },
    unmatchedRequiredFields: {
      headerTitle: "No todas las columnas coincidieron",
      bodyText: "Hay columnas obligatorias que no coinciden o se ignoran. ¿Desea continuar?",
      listTitle: "Columnas que no coinciden:",
      cancelButtonTitle: "Cancelar",
      continueButtonTitle: "Continuar",
    },
    toast: {
      error: "Error",
    },
  },
};

const ImporterConLibreria: React.FC = () => {
  const {postTransactions} = useTransactionsCore()
  const { id } = useParams();
  const { stores, getStores } = useCompanyCore();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (id) {
      getStores(id);
    }
  }, [id, getStores]);

  const storeOptions = useMemo(() => stores.map(store => ({
    label: store.name,
    value: store.name
  })), [stores]);

  const fields = useMemo(() => [
    {
      label: 'Sucursal',
      key: 'sucursal',
      fieldType: {
        type: 'select',
        options: storeOptions,
      },
      alternateMatches: ['sucursal', 'warehouse', 'warehouse_name'],
      validations: [{ rule: 'required', errorMessage: 'La sucursal es requerida.' }],
    },
    {
      label: 'Tipo',
      key: 'tipo',
      alternateMatches: ['tipo de movimiento', 'Tipo', 'tipo movimiento', 'movement_type'],
      fieldType: {
        type: 'select',
        options: [
          { label: 'Venta', value: 'venta' },
          { label: 'Recepción', value: 'recepcion' },
          { label: 'Reservado', value: 'reservado' },
          { label: 'Devolución', value: 'devolucion' },
          { label: 'Ajuste', value: 'ajuste' },
          { label: 'Traslado de entrada', value: 'traslado_entrada' },
          { label: 'Traslado de salida', value: 'traslado_salida' },
          { label: 'Stock Inicial', value: 'stock_inicial' },
        ],
      },
      parseValue: (value: string) => value?.toLowerCase?.().trim(),
      validations: [
        {
          rule: 'required',
          errorMessage: 'El tipo es requerido.',
        },
        {
          rule: 'inOptions',
          errorMessage: 'Debe ser una opción válida.',
        },
      ],
    },
    {
      label: 'Fecha de movimiento',
      key: 'fecha_movimiento',
      alternateMatches: ['date', 'fecha', 'fecha movimiento','Fecha de movimiento'],
      fieldType: { type: 'input' },
      validations: [{ rule: 'required', errorMessage: 'La fecha de movimiento es requerida.' }],
    },
    {
      label: 'Hora de movimiento',
      key: 'hora_movimiento',
      alternateMatches: ['hora', 'hora_movimiento', 'Hora de movimiento','hora de movimiento'],
      fieldType: { type: 'input' },
      validations: [{ rule: 'required', errorMessage: 'La fecha de movimiento es requerida.' }],
    },
    {
      label: 'Transacción',
      key: 'transaccion',
      alternateMatches: ['transaccion', 'transaction', 'transactión'],
      fieldType: { type: 'input' },
    },
    {
      label: 'SKU',
      key: 'sku',
      fieldType: { type: 'input' },
      alternateMatches: ['Sku', 'sku', 'SKU', 'Codigo de barras'],
      validations: [{ rule: 'required', errorMessage: 'El SKU es requerido.' }],
    },
    {
      label: 'Descripción',
      key: 'descripcion',
      alternateMatches: ['descripcion', 'Descripción', 'Description'],
      fieldType: { type: 'input' },
    },
    {
      label: 'Lote/Serie',
      key: 'lote_serie',
      alternateMatches: ['lote_serie', 'Lote', 'Serie','Lote/Serie'],
      fieldType: { type: 'input' },
    },
    {
      label: 'Ubicación',
      key: 'ubicacion',
      alternateMatches: ['ubicacion', 'Ubicación', 'ubicación'],
      fieldType: { type: 'input' },
    },
    {
      label: 'Fecha de expiración',
      key: 'fecha_expiracion',
      alternateMatches: ['Fecha de expiración', 'fecha_expiracion', 'Expiration date', 'expiration_date'],
      fieldType: { type: 'input' },
    },
    {
      label: 'Categoria',
      key: 'categoria',
      alternateMatches: ['categoria', 'Categoria'],
      fieldType: { type: 'input' },
    },
    {
      label: 'Cantidad',
      key: 'cantidad',
      alternateMatches: ['Cantidad', 'cantidad', 'quantity', 'Quantity'],
      fieldType: { type: 'input' },
      validations: [
        { rule: 'required', errorMessage: 'La cantidad es requerida.' },
        { rule: 'regex', value: '^-?[0-9]+(\\.[0-9]+)?$', errorMessage: 'La cantidad debe ser un número válido.' }
      ],
    },
    {
      label: 'Costo',
      key: 'costo',
      alternateMatches: ['Costo', 'Precio', 'cost', 'Precio Unitario'],
      fieldType: { type: 'input' },
      validations: [
        { rule: 'regex', value: '^[-]?[0-9]+$', errorMessage: 'El costo debe ser un número sin puntos o comas' }
      ],
    },
    {
      label: 'Usuario',
      key: 'usuario',
      alternateMatches: ['user', 'usuario', 'Usuario', 'Vendedor'],
      fieldType: { type: 'input' },
    },
  ], [storeOptions]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSubmit = async (data: { validData: string | any[]; }) => {
    console.log("--- REGISTROS PROCESADOS Y VALIDADOS ---");
    console.log(data.validData);
    if (Array.isArray(data.validData) && data.validData.length > 0 && id) {
      const idNumber = Number(id)
      await postTransactions(idNumber,data.validData);
      alert(`¡${data.validData.length} registros importados con éxito!.`);
      setIsOpen(false);
    }
    else{
      console.error("Error al enviar las transacciones:");
      alert("Las importaciones estan vacias, Añade datos a tu excel e intenta nuevamente.");
      setIsOpen(false);
    }
  };

  /**
   * Genera y descarga un archivo .xlsx que sirve como plantilla para el usuario.
   */
  const handleDownloadTemplate = () => {
    const headers = fields.map(field => field.label);
    const worksheet = XLSX.utils.aoa_to_sheet([headers]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Plantilla");
    // Descargar el archivo
    XLSX.writeFile(workbook, "plantilla_importacion.xlsx");
  };

  return (
    <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
      <style>
        {`
          div[role="grid"][aria-colcount="14"] {
            height: 100px;
          }
        `}

      </style>
      <Typography variant="h4" component="h1" gutterBottom>
        Importador de Datos
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
        Sigue los pasos a continuación para importar tus datos de forma masiva.
      </Typography>

      <Card variant="outlined" sx={{ mb: 4, textAlign: 'left' }}>
        <CardContent>
          <Typography variant="h6" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
            Instrucciones de Uso
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><DownloadIcon color="action" /></ListItemIcon>
              <ListItemText primary="Descarga la Plantilla" secondary="Usa nuestro archivo base para asegurar que tus datos tengan el formato correcto." />
            </ListItem>
            <ListItem>
              <ListItemIcon><UploadIcon color="action" /></ListItemIcon>
              <ListItemText primary="Sube tu Archivo" secondary="Haz clic en 'Abrir Importador' y selecciona el archivo que llenaste." />
            </ListItem>
            <ListItem>
              <ListItemIcon><CompareArrowsIcon color="action" /></ListItemIcon>
              <ListItemText primary="Haz Match de Columnas" secondary="El sistema intentará coincidir tus columnas automáticamente. Revisa y ajusta si es necesario." />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="action" /></ListItemIcon>
              <ListItemText primary="Valida y Confirma" secondary="Corrige cualquier error en los datos y confirma la importación final." />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      <Stack direction="row" spacing={2} justifyContent="center">
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadTemplate}
        >
          Descargar Plantilla
        </Button>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<UploadIcon />}
          onClick={() => setIsOpen(true)}
        >
          Abrir Importador
        </Button>
      </Stack>

      {/* 4. Pasa las traducciones al componente */}
      <ReactSpreadsheetImport
        isOpen={isOpen}
        onClose={handleClose}
        onSubmit={handleSubmit}
        fields={fields}
        translations={translations}
        customTheme={{
          components: {
            UploadStep: {
              baseStyle: {
                heading: {
                  fontSize: "3xl",
                  color: "textColor",
                  mb: "2rem",
                },
                title: {
                  fontSize: "3xl",
                  lineHeight: 8,
                  fontWeight: "semibold",
                  color: "textColor",
                },
                subtitle: {
                  lineHeight: 8,
                  color: "subtitleColor",
                  mb: "1rem",
                },
                tableWrapper: {
                  mb: "0.5rem",
                  position: "relative",
                  h: "100px",
                },
                dropzoneText: {
                  size: "xl",
                  lineHeight: 7,
                  fontWeight: "semibold",
                  color: "textColor",
                },
                dropZoneBorder: "rsi.500",
                dropzoneButton: {
                  mt: "1rem",
                },
              },
            },
            Modal: {
              baseStyle: {
                dialog: {
                  borderRadius: "lg",
                  fontSize: "3xl",
                  color: "textColor",
                },
                closeModalButton: {},
                backButton: {
                  gridColumn: "1",
                  gridRow: "1",
                  justifySelf: "start",
                },
                continueButton: {
                  gridColumn: "1 / 3",
                  gridRow: "1",
                  justifySelf: "center",
                },
              },
              variants: {
                rsi: {
                  body: {
                    bg: "background",
                    display: "flex",
                    paddingX: "2rem",
                    paddingY: "2rem",
                    flexDirection: "column",
                    flex: 1,
                    overflow: "auto",
                    height: "100%",
                  },
                  footer: {
                    bg: "secondaryBackground",
                    py: "1.5rem",
                    px: "2rem",
                    justifyContent: "center",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gridTemplateRows: "1fr",
                    gap: "1rem",
                  },
                  dialog: {
                    outline: "unset",
                    minH: "calc(var(--chakra-vh) - 4rem)",
                    maxW: "calc(var(--chakra-vw) - 4rem)",
                    my: "2rem",
                    borderRadius: "3xl",
                    overflow: "hidden",
                  },
                },
              },
            },
          },
        }}
      />
    </Container>
  );
};

export default ImporterConLibreria;